import { api } from '../api';

/** The server's Web Push key, from the softphone credentials (null: push is off on this server). */
let pushKey: string | null = null;

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function sameKey(current: ArrayBuffer | null, wanted: Uint8Array): boolean {
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === wanted.length && bytes.every((b, i) => b === wanted[i]);
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Registers the service worker that shows notifications while the app is closed. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch((error: unknown) => console.warn('Service worker not registered', error));
}

/**
 * Subscribes this browser to push notifications and gives the subscription
 * to the server (for this browser's device). Needs notification permission;
 * without it, or on browsers without push, nothing happens.
 */
export async function ensurePushSubscription(key?: string | null): Promise<boolean> {
  if (key !== undefined) pushKey = key;
  if (!pushKey || !pushSupported() || Notification.permission !== 'granted') return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const wanted = keyBytes(pushKey);
    let subscription = await registration.pushManager.getSubscription();
    // Made with an earlier key: replace it.
    if (subscription && !sameKey(subscription.options.applicationServerKey, wanted)) {
      await subscription.unsubscribe();
      subscription = null;
    }
    subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: wanted });
    await api.savePushSubscription(subscription.toJSON() as Parameters<typeof api.savePushSubscription>[0]);
    return true;
  } catch (error) {
    // Some browsers (or headless ones) have no push service.
    console.warn('Push notifications unavailable in this browser', error);
    return false;
  }
}
